import { Card, Select } from "antd";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useProject } from "../../App/state";
import { useScanSequencesByFilterQuery } from "../../generated/types";
import { DrawingToolContainer } from "../DrawingTool/DrawingToolContainer";
import { getSceneScanningLevels } from "../SequenceMode/utils";
import { selectSceneNodeDrawingStrategy } from "../SpatialConfigurationMode/Strategies/drawingStrategies";
import {
  Drawing,
  ScanSequenceLevelType,
  Scene,
  SpatialConfigTreeNode,
} from "../SpatialConfigurationMode/types";
import { isSceneWithCoordinates } from "../SpatialConfigurationMode/utils";
import styles from "./FloorplanViewer.module.css";

type FloorplanViewerProps = {
  scenes: Scene[];
  selectedSceneIds?: string[];
  drawing: Drawing;
  onDrawingClick?: (x: number, y: number) => void;
  onSelectScene?: (sceneId: string) => void;
  floorplanId?: string;
};

export const FloorplanViewer = (props: FloorplanViewerProps) => {
  const {
    scenes,
    selectedSceneIds,
    drawing,
    onDrawingClick,
    onSelectScene,
    floorplanId,
  } = props;

  const project = useProject();
  const [selectedPhase, setSelectedPhase] = useState<
    ScanSequenceLevelType | "ALL"
  >("ALL");

  const calculateDistance = useCallback(
    (scene1: Scene, scene2: Scene): number => {
      if (!isSceneWithCoordinates(scene1) || !isSceneWithCoordinates(scene2)) {
        return Infinity;
      }
      const dx = scene1.x - scene2.x;
      const dy = scene1.y - scene2.y;
      return Math.sqrt(dx * dx + dy * dy);
    },
    [],
  );

  const findNearestScene = useCallback(
    (targetScene: Scene, availableScenes: Scene[]): Scene | undefined => {
      if (!isSceneWithCoordinates(targetScene)) {
        return availableScenes.find(isSceneWithCoordinates);
      }

      const scenesWithCoordinates = availableScenes.filter(
        isSceneWithCoordinates,
      );
      if (scenesWithCoordinates.length === 0) {
        return undefined;
      }

      let nearestScene = scenesWithCoordinates[0];
      let shortestDistance = calculateDistance(targetScene, nearestScene);

      for (const scene of scenesWithCoordinates) {
        const distance = calculateDistance(targetScene, scene);
        if (distance < shortestDistance) {
          shortestDistance = distance;
          nearestScene = scene;
        }
      }

      return nearestScene;
    },
    [calculateDistance],
  );

  const { data: sequencesData } = useScanSequencesByFilterQuery({
    variables: {
      tenant: project,
      floorplanId: floorplanId!,
    },
    skip: !floorplanId,
  });

  const sceneScanningLevels = useMemo(() => {
    if (!sequencesData?.scanSequencesByFilter) {
      return {};
    }

    const sequences = sequencesData.scanSequencesByFilter.map((seq) => ({
      id: seq.id,
      scanLevel: seq.scanLevel as ScanSequenceLevelType,
      sceneIds: seq.scenes.map((s) => s.id),
    }));

    const sequencesPerPhase: { [key: string]: any[] } = {};
    sequences.forEach((seq) => {
      if (!sequencesPerPhase[seq.scanLevel]) {
        sequencesPerPhase[seq.scanLevel] = [];
      }
      sequencesPerPhase[seq.scanLevel].push(seq);
    });

    return getSceneScanningLevels(
      scenes.map((scene) => scene.id),
      sequencesPerPhase,
    );
  }, [sequencesData, scenes]);

  const filteredScenes = useMemo(() => {
    if (selectedPhase === "ALL") {
      return scenes;
    }

    return scenes.filter((scene) => {
      const scenePhases = sceneScanningLevels[scene.id] || [];
      return scenePhases.includes(selectedPhase);
    });
  }, [scenes, selectedPhase, sceneScanningLevels]);

  useEffect(() => {
    if (!onSelectScene || !selectedSceneIds?.length) {
      return;
    }

    const currentlySelectedSceneId = selectedSceneIds[0];
    const currentlySelectedScene = scenes.find(
      (scene) => scene.id === currentlySelectedSceneId,
    );

    if (
      currentlySelectedScene &&
      !filteredScenes.find((scene) => scene.id === currentlySelectedSceneId)
    ) {
      const nearestScene = findNearestScene(
        currentlySelectedScene,
        filteredScenes,
      );
      if (nearestScene) {
        onSelectScene(nearestScene.id);
      }
    }
  }, [
    filteredScenes,
    selectedSceneIds,
    scenes,
    onSelectScene,
    findNearestScene,
  ]);

  const sceneIdToScene = useMemo(() => {
    const sceneMap: Record<string, Scene> = {};
    filteredScenes.forEach((scene) => {
      sceneMap[scene.id] = scene;
    });
    return sceneMap;
  }, [filteredScenes]);

  const handleDrawingClick = useMemo(() => {
    if (onSelectScene) {
      return selectSceneNodeDrawingStrategy(
        sceneIdToScene,
        (nodes: SpatialConfigTreeNode[]) => {
          if (nodes.length > 0) {
            onSelectScene(nodes[0].key);
          }
        },
        [], // selectedNodes - empty array since we're not tracking multi-selection here
        false, // isMultiSelectMode - false for simple scene selection
      );
    }
    return onDrawingClick;
  }, [sceneIdToScene, onSelectScene, onDrawingClick]);

  const phaseOptions = [
    { value: "ALL", label: "All Phases" },
    { value: ScanSequenceLevelType.Early, label: "Early" },
    { value: ScanSequenceLevelType.Mid, label: "Mid" },
    { value: ScanSequenceLevelType.Late, label: "Late" },
  ];

  return (
    <div className={styles.container}>
      <Card
        title={
          <div className={styles.cardTitle}>
            <span>Floorplan View</span>
            <Select
              showSearch
              optionFilterProp="label"
              value={selectedPhase}
              onChange={setSelectedPhase}
              options={phaseOptions}
              style={{ width: 120 }}
              size="small"
            />
          </div>
        }
        bordered={false}
        className={styles.card}
        bodyStyle={{ padding: 0 }}
      >
        <div className={styles.drawingContainer}>
          <DrawingToolContainer
            scenes={filteredScenes}
            selectedSceneIds={selectedSceneIds}
            drawing={drawing}
            onDrawingClick={handleDrawingClick}
            showOriginAndScale={false}
            enableDrag={true}
            enableMiniMap={true}
          />
        </div>
      </Card>
    </div>
  );
};
